// lib/features/home/home.types.ts
import { ApiResponse } from "../../api/api.types";
import { Product } from "../product/product.types";
// Collection tipini collection feature'dan import ediyoruz — duplicate yok
import { Collection } from "../collection/collection.types";

export type { Collection as HomeCollectionItem };

export interface StoryItemLink {
  type: "product" | "category" | "brand" | "url";
  url: string;
  target_id: number | null;
}

export interface HomeStoryItem {
  id: number;
  title: string;
  button_text: string;
  image_url: string;
  icon_url: string | null;
  link: StoryItemLink;
  type: "image" | "video";
  video_duration: number;
}

export interface HomeStory {
  id: number;
  title: string;
  cover_url: string;
  views_count: number;
  items: HomeStoryItem[];
}

export interface SectionStyle {
  icon: string;
  background_color: string;
  badge_text: string;
  badge_color: string;
}

export interface SectionLink {
  url: string;
  text: string;
}

export interface SectionCountdown {
  ends_at: number | string;
  remaining_seconds: number;
}

export interface SectionItemStats {
  items: string;
  likes: string;
  views: string;
}

// display_type !== "collection" olan section'ların items elemanı
export interface SectionItem {
  id: number;
  title?: string;
  slug: string | null;
  icon?: string;
  image: string;
  products?: Product[];
  items?: unknown[];
  type?: "category" | "custom" | "brand" | "product" | "url";
  name?: string | null;
  isHot?: boolean;
  hot_icon?: string;
  link?: string;
  label?: string;
  sub_images?: string[];
  description?: string;
  background_color?: string;
  text_color?: string;
  avatar?: string;
  stats?: SectionItemStats;
}

// display_type: "collection" olan section — items: Collection[]
export interface CollectionSection {
  id: number;
  title: string;
  slug: string;
  description: string;
  display_type: "collection";
  content_source: "automatic" | "manual" | "hybrid";
  columns: number;
  style: SectionStyle;
  link: SectionLink;
  position: number;
  background_color?: string;
  items: Collection[];
  products: Product[];
  countdown?: SectionCountdown;
}

// Diğer display_type'lar için generic Section
export interface Section {
  id: number;
  title: string;
  slug: string;
  description: string;
  display_type: string;
  content_source: "automatic" | "manual" | "hybrid";
  columns: number;
  style: SectionStyle;
  link: SectionLink;
  position: number;
  background_color?: string;
  items: SectionItem[];
  products: Product[];
  countdown?: SectionCountdown;
}

// display_type: "story" olan section — items: HomeStory[]
export interface StorySection {
  id: number | string;
  title: string;
  slug: string;
  description: string;
  display_type: "story";
  content_source: string;
  columns: number;
  style: SectionStyle;
  link: SectionLink;
  position: number;
  background_color?: string;
  items: HomeStory[];
  products: Product[];
}

// display_type: "slider" olan section — ana sayfanın üst banner alanı.
// items[] tek görselli banner'lar; StoryItemLink ile aynı link şekli.
export interface SliderItem {
  id: number;
  title: string;
  button_text: string;
  /** Asıl alan. `image` ile aynı değeri taşıyor; ikisi de dolu geliyor. */
  image_url: string;
  image: string;
  icon_url: string | null;
  link: StoryItemLink;
  detail: string;
  type: "image" | "video";
  video_duration: number;
}

export interface SliderSection {
  id: number | string;
  title: string;
  slug: string;
  description: string;
  display_type: "slider";
  content_source: string;
  columns: number;
  style: SectionStyle;
  link: SectionLink;
  position: number;
  background_color?: string;
  items: SliderItem[];
  products: Product[];
}

export interface HomeSectionKey {
  key: string;
  id: number | string;
}

export type HomeSectionsResponse = ApiResponse<HomeSectionKey[]>;

export type HomeSectionResponse = ApiResponse<
  Section | CollectionSection | StorySection | SliderSection
>;

export interface HomePageData {
  stories: HomeStory[];
  sections: (Section | CollectionSection)[];
}

export type HomePageResponse = ApiResponse<HomePageData>;

// Type guards
export const isCollectionSection = (
  section: Section | CollectionSection | StorySection | SliderSection,
): section is CollectionSection => section.display_type === "collection";

export const isStorySection = (
  section: Section | CollectionSection | StorySection | SliderSection,
): section is StorySection => section.display_type === "story";

export const isSliderSection = (
  section: Section | CollectionSection | StorySection | SliderSection,
): section is SliderSection => section.display_type === "slider";